/** @prettier */
import React, { useState } from "react";
import "./CreateTest.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useHistory } from "react-router-dom";
import { Form, FormGroup, Button } from "react-bootstrap";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { ControlLabel, FormControl, HelpBlock } from "react-bootstrap";
import { ListGroup, ListGroupItem, ListGroupItemProps } from "react-bootstrap";
import QuestionSelectorAccordion, {
    useCategoriesReducer,
} from "./CategoriesAccordion";
import { createTest } from "./createTestApi";
import QuestionTypeFormGroup, {
    useQuestionTypeCounts,
} from "./QuestionTypesSelector";
import { QuestionType, TestMode, TestBackground } from "../models";

type State<T> = [T, (t: T) => void];

function listGroupSelection<T>(
    label: string,
    tooltip: string,
    value: T,
    [currentVal, setValue]: State<T>,
) {
    const props: ListGroupItemProps =
        value === currentVal
            ? { active: true }
            : { onClick: () => setValue(value) };
    return (
        <ListGroupItem {...props}>
            {label}
            <OverlayTrigger
                placement="left"
                overlay={<Tooltip>{tooltip}</Tooltip>}
            >
                <FontAwesomeIcon icon="question-circle" />
            </OverlayTrigger>
        </ListGroupItem>
    );
}

const studyTooltip =
    "An untimed test with answers displayed after each question";
const timedTooltip =
    "A timed test with answers displayed at the end of the test";
const untimedTooltip =
    "An untimed test with answers displayed at the end of the test.";

type TestModeFormGroupProps = { testModeState: State<TestMode> };
const TestModeFormGroup = ({
    testModeState: testMode,
}: TestModeFormGroupProps) => (
    <FormGroup bsClass="test-mode-list-group">
        <ControlLabel>Test Mode</ControlLabel>
        <ListGroup>
            {listGroupSelection("Study", studyTooltip, "STUDY", testMode)}
            {listGroupSelection("Timed", timedTooltip, "TIMED", testMode)}
            {listGroupSelection("Untimed", untimedTooltip, "UNTIMED", testMode)}
        </ListGroup>
    </FormGroup>
);

export type CreateTestProps = {
    activeSubscription: { product: { uuid: string }; userUuid: string };
};
const CreateTest = ({ activeSubscription }: CreateTestProps) => {
    const [testName, setTestName] = useState("");
    const [numberOfQuestions, setNumberOfQuestions] = useState(50);
    const testModeState = useState<TestMode>("STUDY");
    const questionTypeState = useState<QuestionType>("unused");
    const testBgState = useState<TestBackground>("qbank");
    const selectionState = useCategoriesReducer();

    const [selectedCategories] = selectionState;
    const questionTypeCounts = useQuestionTypeCounts(
        activeSubscription.userUuid,
        selectedCategories,
    );

    const [showingErrors, setShowingErrors] = useState(false);

    const enoughQuestionsSelected =
        questionTypeCounts.state === "loaded"
            ? questionTypeCounts.data[questionTypeState[0]] >= numberOfQuestions
            : false;
    const history = useHistory();
    const submit = async () => {
        if (!enoughQuestionsSelected) {
            return setShowingErrors(true);
        }
        setShowingErrors(false);

        createTest({
            productUuid: activeSubscription.product.uuid,
            name: testName,
            numberOfQuestions,
            mode: testModeState[0],
            categories: Array.from(selectedCategories),
            questionType: questionTypeState[0],
            background: testBgState[0],
        }).then(() => history.push("/tests"));
    };

    return (
        <div id="create-test">
            <div className="dashboard-header">
                <h3 className="dashboard-title">Create Test</h3>
            </div>
            <Form onSubmit={() => setShowingErrors(true)}>
                <FormGroup controlId="testName">
                    <ControlLabel>Test Name</ControlLabel>
                    <FormControl
                        value={testName}
                        onChange={(e) => {
                            setTestName((e.target as any).value);
                        }}
                        type="text"
                        className="form-control"
                        placeholder="Test Name"
                    />
                </FormGroup>
                <FormGroup
                    controlId="numQuestions"
                    validationState={
                        showingErrors && !enoughQuestionsSelected
                            ? "error"
                            : undefined
                    }
                >
                    <ControlLabel>Number of Questions</ControlLabel>
                    <FormControl
                        type="number"
                        className="form-control"
                        value={numberOfQuestions}
                        onChange={(e) =>
                            setNumberOfQuestions((e.target as any).value)
                        }
                        min={1}
                        max={250}
                    />
                    <HelpBlock>
                        {showingErrors && !enoughQuestionsSelected
                            ? "Not enough questions selected"
                            : "Number of questions, between 1 and 250"}
                    </HelpBlock>
                </FormGroup>
                <TestModeFormGroup testModeState={testModeState} />
                <FormGroup>
                    <ControlLabel>Question Categories</ControlLabel>
                    <QuestionSelectorAccordion
                        state={selectionState}
                        productId={activeSubscription.product.uuid}
                    />
                </FormGroup>
                <QuestionTypeFormGroup
                    counts={questionTypeCounts}
                    state={questionTypeState}
                />
                <FormGroup>
                    <ControlLabel>Test Background</ControlLabel>
                    <ListGroup>
                        {listGroupSelection(
                            "QBank.com Default",
                            "A clean, simple interface",
                            "qbank",
                            testBgState,
                        )}
                        {listGroupSelection(
                            "USMLE",
                            "Mimics the professional test interface.",
                            "usmle",
                            testBgState,
                        )}
                        {listGroupSelection(
                            "Night Mode",
                            "Reduces the screen brightness for better sleep, less eye strain, and longer battery life.",
                            "nightmode",
                            testBgState,
                        )}
                    </ListGroup>
                </FormGroup>
                <Button
                    onClick={(e) => {
                        e.preventDefault();
                        submit();
                    }}
                    bsStyle="primary"
                    type="submit"
                >
                    Create Test
                </Button>
            </Form>
        </div>
    );
};

export default CreateTest;
